/** The dynamic style blocks — direct ports from FirstLightStyle.swift.
 * Each function mutates the style JSON in place, same order and same
 * anchoring rules as the Swift original. The color tables (NIGHT_MAP,
 * NIGHT_LAYER_OVERRIDES, DUSTLARK_TOPO_PAINT, GEOLOGY_PALETTE) must match
 * the Swift tables verbatim — they are transcriptions, not designs. */

import { layerURL, type StateManifest } from "../catalog";

type AnyLayer = Record<string, any>;
type StyleJSON = {
  sources: Record<string, any>;
  layers: AnyLayer[];
  [k: string]: any;
};

// ---------------------------------------------------------------- tables
// Transcribed from FirstLightStyle.swift (nightMap, ~line 88). Keys are
// day hexes as they appear in the template; values are the Night Stage
// equivalents. TRANSCRIBE-EXACT: see Swift for the authoritative list.
import NIGHT_MAP from "./tables/night-map.json";
import NIGHT_LAYER_OVERRIDES from "./tables/night-layer-overrides.json";
import DUSTLARK_TOPO_PAINT from "./tables/dustlark-topo-paint.json";
import GEOLOGY_PALETTE from "./tables/geology-palette.json";
// Symbology v2 (tools/symbology/apply_v2.py): paint/layout for the BLM and
// hiking layers, which live in sources ("blm", "hiking") that aren't part
// of the static template — generated from the SAME classes table
// (symbology-v2.json) the template's own MVUM layers come from, so BLM
// roads/trails and hiking trails draw in the identical v2 hex instead of a
// hand-typed one. casing-pairs.json (casing layer id -> the fill id it
// wraps) drives the imagery two-tone construction in applyLook, below.
import INJECTED_NETWORK from "./tables/injected-network.json";
import CASING_PAIRS from "./tables/casing-pairs.json";
// Map markings (tables/markings.json, read through mapMarkings.ts so the
// picker UI and the style builder can't disagree about what the table
// says) — see the applyMarkings section at the bottom of this file.
import { MARKINGS, MARKINGS_TABLE, type MapMarkings } from "./mapMarkings";

// ------------------------------------------------------------------ night

/** Recolor the whole style for the Night Stage: every known day hex swaps
 * for its night hex; per-layer overrides win over the global map. Ported
 * from nightify()/nightValue() — line for line, including two easy-to-miss
 * Swift semantics: (1) only paint/layout keys whose NAME contains "color"
 * are ever touched (a "line-width" or "line-dasharray" value that happens
 * to look like a color string is left alone); (2) the override table is
 * keyed by "layerID|dayHex" — the lowercased DAY COLOR itself, not the
 * property name — so the same day hex can resolve differently per layer. */
export function applyNight(style: StyleJSON) {
  const overrides = NIGHT_LAYER_OVERRIDES as Record<string, string>;
  const map = NIGHT_MAP as Record<string, string>;

  const nightValue = (value: any, layerID: string): any => {
    if (typeof value === "string") {
      const key = value.toLowerCase();
      if (key.startsWith("#")) {
        return overrides[`${layerID}|${key}`] ?? map[key] ?? value;
      }
      return value;
    }
    if (Array.isArray(value)) {
      return value.map((v) => nightValue(v, layerID));
    }
    return value;
  };

  for (const layer of style.layers) {
    const id = (layer.id as string) ?? "";
    for (const section of ["paint", "layout"]) {
      const props = layer[section];
      if (!props) continue;
      for (const [k, v] of Object.entries(props)) {
        if (!k.includes("color")) continue;
        props[k] = nightValue(v, id);
      }
    }
  }
}

// ------------------------------------------------------------- blm/hiking

/** BLM public motorized routes — Symbology v2 retires the dawnsky-blue
 * agency color outright (SPEC.md section 1: "A BLM road that a car can
 * drive looks like a Forest Service road that a car can drive"): a BLM
 * "Road" draws exactly like graded-road, a "Primitive Road" like
 * highclear-road, and a trail (klass trail_limited) like a full-size 4x4
 * trail, each with the SAME casing its MVUM counterpart gets — paint comes
 * straight from injected-network.json (tools/symbology/apply_v2.py), so
 * there is no hex to hand-type here and nothing to keep in sync by hand.
 * Because the color is now one of the day hexes nightify's global sweep
 * already knows (it's the same hex the MVUM class uses), this no longer
 * needs its own `night` branch at all — port of the Swift block at
 * ~line 374, minus the blue. */
const INJECTED = INJECTED_NETWORK as Record<
  string,
  { "source-layer": string; filter: unknown; minzoom?: number; layout: AnyLayer; paint: AnyLayer }
>;

function injectedLayer(id: string, source: string): AnyLayer {
  const row = INJECTED[id];
  const layer: AnyLayer = {
    id,
    type: "line",
    source,
    "source-layer": row["source-layer"],
    filter: row.filter,
    layout: { ...row.layout },
    paint: { ...row.paint },
  };
  if (row.minzoom !== undefined) layer.minzoom = row.minzoom;
  return layer;
}

export function injectBLM(style: StyleJSON, m: StateManifest, _night: boolean) {
  const url = layerURL(m, "blm");
  if (!url) return;
  style.sources["blm"] = { type: "vector", url: `pmtiles://${url}` };

  const closedAt = style.layers.findIndex((l) => l.id === "closed-mvum-tick");
  const closed = [injectedLayer("closed-blm", "blm"), injectedLayer("closed-blm-tick", "blm")];
  if (closedAt >= 0) style.layers.splice(closedAt + 1, 0, ...closed);
  else style.layers.push(...closed);

  // Each pair is inserted just under its MVUM counterpart's casing —
  // same "just under the MVUM casings" slot the v1 comment described,
  // extended per class so BLM never draws OVER the route it stands in for.
  const insertBefore = (id: string, layers: AnyLayer[]) => {
    const at = style.layers.findIndex((l) => l.id === id);
    if (at >= 0) style.layers.splice(at, 0, ...layers);
    else style.layers.push(...layers);
  };
  insertBefore("mvum-trail-4x4-casing", [injectedLayer("blm-trail-casing", "blm"), injectedLayer("blm-trail", "blm")]);
  insertBefore("mvum-road-hc-casing", [
    injectedLayer("blm-primitive-casing", "blm"),
    injectedLayer("blm-primitive", "blm"),
  ]);
  insertBefore("mvum-road-casing", [injectedLayer("blm-road-casing", "blm"), injectedLayer("blm-road", "blm")]);
}

/** NFS hiking trails — Symbology v2's nonmotor class (foot dots, horse
 * dash-dot-dot, shared cream casing), same rule as BLM: paint/layout come
 * from injected-network.json so the hiking source draws in the identical
 * v2 hex the template's own path/mvum-road-casing-adjacent classes use.
 * Port of the Swift block at ~line 414. Swift's `hike` (Hike Mode) flag
 * widens the line/brings labels in earlier; BuildOptions has no hike flag
 * yet, so this always renders Swift's `hike == false` branch.
 *
 * `look` gates tuning #2 (Sep 21 review): a dense hiking archive (Uinta NF
 * near Kyhv Peak) read as noise on the paper looks at z14 -- the cream
 * casing is held back to z15+ and the fill opacity is stepped down below
 * z15 on every look EXCEPT imagery, which keeps its two-tone casing (and
 * full opacity) at the class's normal minzoom per SPEC.md section 5 ("every
 * network class gets a two-tone casing over imagery... casing and fill
 * together"). This can't live in the SPEC JSON's flat minzoom/opacity
 * fields (those bake into injected-network.json once, for every look) so
 * it's applied here, after injectedLayer() builds the paper/topo/night
 * baseline, and before applyLook's imagery branch converts hiking-line-
 * casing into the sleeve/core pair. */
export function injectHiking(style: StyleJSON, m: StateManifest, look: string) {
  const url = layerURL(m, "hiking");
  if (!url) return;
  style.sources["hiking"] = { type: "vector", url: `pmtiles://${url}` };
  // our hiking layer supersedes the basemap's generic foot paths
  style.layers = style.layers.filter((l) => l.id !== "path" && l.id !== "path-casing" && l.id !== "path-sleeve");

  const insertAt = style.layers.findIndex((l) => l.id === "mvum-road-casing");
  const hikeLayers = [
    injectedLayer("hiking-line-casing", "hiking"),
    injectedLayer("hiking-line", "hiking"),
    injectedLayer("hiking-line-horse", "hiking"),
  ];
  if (look !== "imagery") {
    const casing = hikeLayers.find((l) => l.id === "hiking-line-casing");
    if (casing) casing.minzoom = 15;
    const OPACITY_BELOW_15 = ["step", ["zoom"], 0.85, 15, 1] as const;
    for (const id of ["hiking-line", "hiking-line-horse"]) {
      const fill = hikeLayers.find((l) => l.id === id);
      if (fill) fill.paint = { ...fill.paint, "line-opacity": OPACITY_BELOW_15 };
    }
  }
  if (insertAt >= 0) style.layers.splice(insertAt, 0, ...hikeLayers);
  else style.layers.push(...hikeLayers);

  style.layers.push({
    id: "hiking-label",
    type: "symbol",
    source: "hiking",
    "source-layer": "hiking_trails",
    minzoom: 13.5,
    filter: ["all", ["!=", ["get", "use"], "moto"], ["has", "name"], ["!=", ["get", "name"], ""]],
    layout: {
      "text-field": ["get", "name"],
      "text-font": ["Noto-Sans-Regular"],
      "symbol-placement": "line",
      "text-size": ["interpolate", ["linear"], ["zoom"], 13.5, 13, 16, 15],
      "text-padding": 2.34,
    },
    paint: {
      "text-color": "#1E4F33",
      "text-halo-color": "#F6EFE2",
      "text-halo-width": 1.95,
    },
  });
}

// ------------------------------------------------------------------ looks

const isBaseCartography = (l: AnyLayer) =>
  l.source === "basemap" ||
  String(l.id ?? "").startsWith("own-") ||
  l.id === "contour" ||
  l.id === "contour-index" ||
  l.id === "contour-label";

/** Dark sleeve for trail/route dashes over imagery — the same soilink ink
 * the two-tone casing construction in applyLook's imagery branch (below)
 * uses for every network class's CORE ring. Port of
 * FirstLightStyle.imageryCasingInk. */
const IMAGERY_CASING_INK = "#3B3226";

/** Text-halo-width targets for every HERO label left standing over the
 * Imagery look (own-label/own-label-line don't need an entry — applyLook's
 * imagery branch removes every own-* layer outright, below). Historic and
 * Hillshade tune nothing here; they simply inherit the base template's own
 * dark-ink-text + light-halo (#F6EFE2) rule. Symbology v2 (SPEC.md section
 * 7's halo rule): imagery's fraction is 0.22 of text-size (paper is 0.15),
 * so every value here is `round(0.22 * templateSize, 2)` at the layer's own
 * Standard-theme template size (its smallest zoom stop, for the
 * zoom-interpolated ones) — this REPLACES v1's ad hoc per-tier +0.6/+0.4/
 * +0.3/+0.2 px boosts (poi-label is gone — split into camp-label/
 * ridb-facility-label; only camp-label re-joins the hero tier, per SPEC.md
 * section 11). Port of FirstLightStyle.imageryLabelHaloWidth. */
const IMAGERY_LABEL_HALO_WIDTH: Record<string, number> = {
  "mvum-road-label": 2.86, // 0.22 * 13
  "mvum-trail-label": 2.86,
  "hiking-label": 2.86,
  "street-label": 2.86,
  "water-name": 2.64, // 0.22 * 12
  "place-village": 2.86,
  "place-town": 3.08, // 0.22 * 14 (smallest stop)
  "peak-label": 2.64,
  "stream-name-river": 2.42, // 0.22 * 11
  "stream-name": 2.42,
  "camp-label": 2.64,
  "fuel-label": 2.64,
  "viewpoint-label": 2.64,
  "trailhead-parking-label": 2.64,
};

/** All look mutations, same order/rules as Swift: hillshade, historic,
 * ustopo (partition!), dustlarkTopo (also under ustopo), imagery. */
export function applyLook(
  style: StyleJSON,
  m: StateManifest,
  look: string,
  night: boolean,
) {
  const bgIndex = () => style.layers.findIndex((l) => l.id === "background");

  if (look === "hillshade") {
    const hs = layerURL(m, "hillshade");
    if (hs) {
      style.sources["hillshade"] = { type: "raster", url: `pmtiles://${hs}`, tileSize: 256 };
      style.layers = style.layers.filter(
        (l) => !["landcover-wood", "landcover-grass", "landuse-residential"].includes(l.id),
      );
      const bg = bgIndex();
      if (bg >= 0) {
        style.layers.splice(bg + 1, 0, {
          id: "hillshade-raster",
          type: "raster",
          source: "hillshade",
          paint: { "raster-opacity": night ? 0.35 : 0.85 },
        });
        const lidar = layerURL(m, "lidar");
        if (lidar) {
          style.sources["lidar"] = { type: "raster", url: `pmtiles://${lidar}`, tileSize: 256 };
          style.layers.splice(bg + 2, 0, {
            id: "lidar-raster",
            type: "raster",
            source: "lidar",
            minzoom: 12.5,
            paint: { "raster-opacity": night ? 0.35 : 0.9 },
          });
        }
      }
    }
  }

  if (look === "historic") {
    const h = layerURL(m, "historic");
    if (h) {
      style.sources["historic"] = { type: "raster", url: `pmtiles://${h}`, tileSize: 256 };
      style.layers = style.layers.filter((l) => !isBaseCartography(l));
      const bg = bgIndex();
      if (bg >= 0) {
        style.layers.splice(bg + 1, 0, {
          id: "historic-raster",
          type: "raster",
          source: "historic",
          paint: { "raster-opacity": 1.0 },
        });
      }
    }
  }

  if (look === "ustopo") {
    const u = layerURL(m, "ustopo");
    if (u) {
      style.sources["ustopo"] = { type: "raster", url: `pmtiles://${u}`, tileSize: 256 };
      // Stable PARTITION, not one insertion index — see the long Swift
      // comment: base cartography keeps its order below the scan, every
      // trails/BLM/hiking/POI/parcel/slope layer keeps its order above it,
      // so the legal network never drowns under the opaque raster.
      const bg = bgIndex();
      if (bg >= 0) {
        const rest = style.layers.slice(bg + 1);
        const base = rest.filter(isBaseCartography);
        const overlay = rest.filter((l) => !isBaseCartography(l));
        style.layers = [
          ...style.layers.slice(0, bg + 1),
          ...base,
          { id: "ustopo-raster", type: "raster", source: "ustopo", paint: { "raster-opacity": 1.0 } },
          ...overlay,
        ];
      }
    }
  }

  if (look === "dustlarkTopo" || look === "ustopo") {
    // Paper ground — warm white, not First Light's cream.
    const bg = bgIndex();
    if (bg >= 0) {
      style.layers[bg].paint = {
        ...(style.layers[bg].paint ?? {}),
        "background-color": "#FDFDF8",
      };
    }
    // Symbology v2 (SPEC.md section 6): "the single `contour` layer and the
    // `dtopo-contour-*` injection both go away" — `contour`/`contour-index`/
    // `contour-label` now live in the template PERMANENTLY (idx==0 / idx==1
    // / idx==1-symbol) and every look, this one included, recolors them
    // through the SAME DUSTLARK_TOPO_PAINT sweep below as every other
    // layer, instead of swapping in a parallel USGS-styled ladder. Nothing
    // to splice here any more.
    // Paper recolors, keyed by the same layer ids every look uses.
    const paint = DUSTLARK_TOPO_PAINT as Record<string, Record<string, any>>;
    for (const layer of style.layers) {
      const overrides = paint[layer.id as string];
      if (!overrides) continue;
      layer.paint = { ...(layer.paint ?? {}), ...overrides };
    }
  }

  if (look === "imagery") {
    const i = layerURL(m, "imagery");
    // Statewide, coarser-zoom companion to the corridor-scale `imagery`
    // layer — a future state may ship this alone (base-only), so the
    // branch runs off EITHER being present, not just `i`.
    const ib = layerURL(m, "imagerybase");
    if (i || ib) {
      style.layers = style.layers.filter(
        (l) =>
          !["landcover-wood", "landcover-grass", "landuse-residential"].includes(l.id) &&
          !String(l.id ?? "").startsWith("own-"),
      );
      // above basemap FILLS, below its first line/symbol
      let idx = style.layers.findIndex(
        (l) => l.source === "basemap" && (l.type === "line" || l.type === "symbol"),
      );
      if (idx < 0) {
        const bg = bgIndex();
        idx = bg >= 0 ? bg + 1 : style.layers.length;
      }
      if (i) {
        style.sources["imagery"] = { type: "raster", url: `pmtiles://${i}`, tileSize: 256 };
        style.layers.splice(idx, 0, {
          id: "imagery-raster",
          type: "raster",
          source: "imagery",
          paint: { "raster-opacity": 1.0 },
        });
      }
      // Statewide base UNDER the corridor detail: past the base's own max
      // zoom it overzooms softly instead of the map going blank, while the
      // corridor stays crisp on top. Inserted directly below imagery-raster
      // when both exist; where imagery-raster doesn't exist (base-only
      // state), it takes the same slot imagery-raster would have used.
      if (ib) {
        style.sources["imagerybase"] = { type: "raster", url: `pmtiles://${ib}`, tileSize: 256 };
        const detailIdx = style.layers.findIndex((l) => l.id === "imagery-raster");
        style.layers.splice(detailIdx >= 0 ? detailIdx : idx, 0, {
          id: "imagery-base-raster",
          type: "raster",
          source: "imagerybase",
          paint: { "raster-opacity": 1.0 },
        });
      }
      // Symbology v2 (SPEC.md sections 3.3/5): every network class gets a
      // TWO-TONE casing over imagery instead of one dark ink sweep — a
      // cream #FFFDF7 SLEEVE (widest) under a dark #3B3226 CORE under the
      // colored fill, because a single dark ring reads on timber but can
      // vanish into bright slickrock, while a single cream one reads on
      // slickrock but vanishes into a cream road cutting through snow. The
      // existing "-casing" layer BECOMES the core (repainted + resized, so
      // ImageryLegibilityTests-equivalent orphan-casing checks keep the same
      // target); casing-pairs.json (tools/symbology/apply_v2.py) names each
      // casing's own fill layer, whose CURRENT line-width (already resolved
      // for this look/night/markings — this runs after injectBLM/
      // injectHiking, before applyMarkings) is the base the two rims are
      // added to. hiking-line-casing and blm-*-casing are already baked in
      // by injectHiking/injectBLM on every look now (Symbology v2 gives BLM
      // and hiking a casing on paper too, not just imagery), so this no
      // longer needs to clone one into existence here.
      const CORE_RIM = 1.2;
      const SLEEVE_RIM = 0.8;
      const bumpInterpolate = (expr: unknown, add: number): unknown => {
        const round4 = (n: number) => Math.round(n * 10000) / 10000;
        if (typeof expr === "number") return round4(expr + add);
        if (!Array.isArray(expr) || expr[0] !== "interpolate") return expr;
        const out = expr.slice(0, 3);
        for (let i = 3; i + 1 < expr.length; i += 2) out.push(expr[i], round4((expr[i + 1] as number) + add));
        return out;
      };
      const pairs = CASING_PAIRS as Record<string, string>;
      for (const casingId of Object.keys(pairs)) {
        const coreIdx = style.layers.findIndex((l) => l.id === casingId);
        if (coreIdx < 0) continue;
        const fillLayer = style.layers.find((l) => l.id === pairs[casingId]);
        const fillWidth = fillLayer?.paint?.["line-width"];
        if (fillWidth === undefined) continue;
        const core = style.layers[coreIdx];
        core.paint = {
          ...(core.paint ?? {}),
          "line-color": IMAGERY_CASING_INK,
          "line-opacity": 0.9,
          "line-width": bumpInterpolate(fillWidth, 2 * CORE_RIM),
        };
        delete core.paint["line-dasharray"]; // casings are solid on every ground
        const sleeve: AnyLayer = {
          id: `${pairs[casingId]}-sleeve`,
          type: "line",
          source: core.source,
          "source-layer": core["source-layer"],
          filter: core.filter,
          layout: core.layout,
          paint: {
            "line-color": "#FFFDF7",
            "line-opacity": 0.9,
            "line-width": bumpInterpolate(fillWidth, 2 * (CORE_RIM + SLEEVE_RIM)),
          },
        };
        if (core.minzoom !== undefined) sleeve.minzoom = core.minzoom;
        style.layers.splice(coreIdx, 0, sleeve);
      }
      // Any other "-casing"-suffixed layer not in casing-pairs.json (there
      // are none left in the v2 template — every network class with a
      // casing is in the table — but a future one-off casing falls back to
      // the single dark ink rather than silently keeping its paper/cream
      // color over a photograph).
      for (const layer of style.layers) {
        const id = String(layer.id ?? "");
        if (id.endsWith("-casing") && !pairs[id]) {
          layer.paint = { ...(layer.paint ?? {}), "line-color": IMAGERY_CASING_INK };
        }
      }
      // Every satellite-visible label's halo widens per size class — dark
      // ink text and #F6EFE2 halo color both stay exactly what every other
      // daylight look already uses, only the width moves.
      for (const layer of style.layers) {
        const id = String(layer.id ?? "");
        const width = IMAGERY_LABEL_HALO_WIDTH[id];
        if (width !== undefined) {
          layer.paint = { ...(layer.paint ?? {}), "text-halo-width": width };
        }
      }
      // Symbology v2 tuning #4 (Sep 21 review): contour/contour-index got a
      // lighter, thinner, lower-opacity treatment on the paper/topo looks
      // (the template's own baked values, since those grounds don't touch
      // this layer). "Over imagery the current treatment is fine — leave
      // it": width/opacity are baked once for every ground, so imagery is
      // restored here to what it rendered before that pass.
      const contour = style.layers.find((l) => l.id === "contour");
      if (contour) {
        contour.paint = {
          ...contour.paint,
          "line-opacity": 0.9,
        };
      }
      const contourIndex = style.layers.find((l) => l.id === "contour-index");
      if (contourIndex) {
        contourIndex.paint = {
          ...contourIndex.paint,
          "line-width": ["interpolate", ["linear"], ["zoom"], 11, 1.2, 14, 1.7, 16, 2.3],
        };
      }
    }
  }
}

// ---------------------------------------------------------------- geology

/** Bedrock geology fills/contacts/labels, hidden by default (toggle).
 * Port of the Swift block at ~line 754; palette transcribed from
 * GeologyCard.paletteHex. */
export function injectGeology(
  style: StyleJSON,
  m: StateManifest,
  geoNight: boolean,
  night: boolean,
) {
  const url = layerURL(m, "geology");
  if (!url) return;
  style.sources["geology"] = { type: "vector", url: `pmtiles://${url}` };
  const pal = (GEOLOGY_PALETTE as { day: string[]; night: string[] })[
    geoNight ? "night" : "day"
  ];
  const fillColor: any[] = ["match", ["get", "gc"]];
  pal.forEach((hex, i) => fillColor.push(i, hex));
  fillColor.push(pal[9]);
  const fillOpacity = [
    "interpolate",
    ["linear"],
    ["zoom"],
    6,
    night ? 0.18 : 0.2,
    9,
    night ? 0.3 : 0.34,
    11,
    night ? 0.42 : 0.5,
  ];
  const fill: AnyLayer = {
    id: "geology-fill",
    type: "fill",
    source: "geology",
    "source-layer": "geology",
    layout: { visibility: "none" },
    paint: { "fill-color": fillColor, "fill-opacity": fillOpacity, "fill-antialias": true },
  };
  const outline: AnyLayer = {
    id: "geology-outline",
    type: "line",
    source: "geology",
    "source-layer": "geology",
    minzoom: 9,
    layout: { visibility: "none" },
    paint: {
      "line-color": night ? "#12100c" : "#5c4a2f",
      "line-opacity": night ? 0.5 : 0.35,
      "line-width": ["interpolate", ["linear"], ["zoom"], 7, 0.2, 12, 0.8],
    },
  };
  const firstBasemapLine = style.layers.findIndex(
    (l) => l.source === "basemap" && (l.type === "line" || l.type === "symbol"),
  );
  let idx = firstBasemapLine;
  if (idx < 0) {
    const lastRaster = style.layers
      .map((l, i) => ({ l, i }))
      .filter(({ l }) => String(l.id ?? "").endsWith("-raster"))
      .map(({ i }) => i)
      .pop();
    idx =
      lastRaster !== undefined
        ? lastRaster + 1
        : style.layers.findIndex((l) => l.id === "background") + 1;
  }
  style.layers.splice(Math.max(idx, 0), 0, fill, outline);

  const classWords = [
    "Surficial",
    "Clastic",
    "Carbonate",
    "Evaporite",
    "Volcanic",
    "Intrusive",
    "Metamorphic",
    "Schist",
    "Water",
    "Bedrock",
  ];
  const textField: any[] = ["match", ["get", "gc"]];
  classWords.forEach((w, i) => textField.push(i, w));
  textField.push("Bedrock");
  style.layers.push({
    id: "geology-label",
    type: "symbol",
    source: "geology",
    "source-layer": "geology",
    minzoom: 8.5,
    layout: {
      visibility: "none",
      "text-field": textField,
      "text-font": ["Noto-Sans-Regular"],
      // Symbology v2 typography row "geology-label": 8.5 -> 12, 12 -> 13,
      // 14 -> 14 (+1 pt at the small end; capped at 14 so a reference label
      // never out-ranks the hero tier at the same zoom).
      "text-size": ["interpolate", ["linear"], ["zoom"], 8.5, 12, 12, 13, 14, 14],
      "symbol-placement": "point",
      "text-allow-overlap": false,
      "text-padding": 22,
      "text-transform": "uppercase",
      "text-letter-spacing": 0.08,
    },
    // Swift keys this label's paint off the raw `night` flag, not
    // `geoNight` (contrast the fill/outline above, and the palette pick
    // itself, which all use geoNight) — verified against the literal
    // source at ~line 855; ported as written, quirk and all.
    paint: {
      "text-color": night ? "#e8dfd0" : "#4a3f30",
      "text-halo-color": night ? "#12100c" : "#f6efe2",
      "text-halo-width": 1.7,
      "text-opacity": night ? 0.85 : 0.9,
    },
  });
}

// -------------------------------------------------------------------- plss

/** PLSS Township/Range/Section grid (item 17, feature catalog 1f: "how the
 * rural West actually describes land"), hidden by default (toggle). Port
 * of the Swift block at ~line 963 in FirstLightStyle.swift. Lines only —
 * no fill, unlike ownership/geology/parcels: PLSS's whole job is a
 * reference grid, and a translucent fill would just compete with every
 * other fill already on the map for no benefit. Townships dashed at full
 * soilink weight (the coarser, primary grid unit); sections solid but
 * lighter — thinner and lower opacity — so the finer subdivision nested
 * inside doesn't overpower the township lines around it.
 *
 * Label font: the task spec called for "mono" section labels, but the web
 * template's own glyph stack (same offline-safe set the app bundles) only
 * carries Noto Sans — every other small map label already uses
 * Noto-Sans-Regular (mvum-road-label, hiking-label), so PLSS follows suit
 * rather than shipping an invisible label. Halo width 2.0 sits in the
 * generous middle of IMAGERY_LABEL_HALO_WIDTH's range rather than the
 * thinner ~1.4-1.8 default, since a reference-grid label needs to hold up
 * over every look/ground, not just the cartographic ones that table was
 * tuned for. */
export function injectPLSS(style: StyleJSON, m: StateManifest, night: boolean) {
  const url = layerURL(m, "plss");
  if (!url) return;
  style.sources["plss"] = { type: "vector", url: `pmtiles://${url}` };

  // Theme.soilink's own day/night pair — not a new color, the app's
  // existing ink token.
  const ink = night ? "#E2D9CB" : "#3B3226";
  // paledust/near-black paper tone, matching every other label's
  // halo-vs-background pairing in this style.
  const haloColor = night ? "#171512" : "#F6EFE2";

  const townshipLine: AnyLayer = {
    id: "plss-township-line",
    type: "line",
    source: "plss",
    "source-layer": "plss_townships",
    minzoom: 8,
    layout: { visibility: "none", "line-cap": "round", "line-join": "round" },
    paint: {
      "line-color": ink,
      "line-width": ["interpolate", ["linear"], ["zoom"], 8, 0.5, 14, 1.3],
      "line-opacity": 0.85,
      "line-dasharray": [3, 2],
    },
  };
  const sectionLine: AnyLayer = {
    id: "plss-section-line",
    type: "line",
    source: "plss",
    "source-layer": "plss_sections",
    minzoom: 11,
    layout: { visibility: "none", "line-cap": "round", "line-join": "round" },
    paint: {
      "line-color": ink,
      "line-width": ["interpolate", ["linear"], ["zoom"], 11, 0.4, 15, 0.9],
      "line-opacity": 0.5,
    },
  };
  // Same slot geology's fill/outline claim: above the base fills/rasters,
  // below trails/labels (or, when a look stripped the basemap entirely,
  // just above the topmost raster).
  const firstBasemapLine = style.layers.findIndex(
    (l) => l.source === "basemap" && (l.type === "line" || l.type === "symbol"),
  );
  let idx = firstBasemapLine;
  if (idx < 0) {
    const lastRaster = style.layers
      .map((l, i) => ({ l, i }))
      .filter(({ l }) => String(l.id ?? "").endsWith("-raster"))
      .map(({ i }) => i)
      .pop();
    idx =
      lastRaster !== undefined
        ? lastRaster + 1
        : style.layers.findIndex((l) => l.id === "background") + 1;
  }
  style.layers.splice(Math.max(idx, 0), 0, townshipLine, sectionLine);

  style.layers.push({
    id: "plss-section-label",
    type: "symbol",
    source: "plss",
    "source-layer": "plss_sections",
    minzoom: 12,
    layout: {
      visibility: "none",
      "text-field": ["get", "label"],
      "text-font": ["Noto-Sans-Regular"],
      "symbol-placement": "point",
      // Symbology v2 typography row "plss-section-label": 9.5 -> 10.5 (+1 pt).
      "text-size": 10.5,
      "text-allow-overlap": false,
      "text-padding": 8,
    },
    paint: {
      "text-color": ink,
      "text-halo-color": haloColor,
      "text-halo-width": 2.0,
    },
  }); // topmost — the overlay is a deliberate toggle
}

// --------------------------------------------------------------- live sun

/** Live-sun hillshade over the raster-dem — outdoors look only. Port of
 * the Swift block at ~line 905. */
export function injectLiveSun(
  style: StyleJSON,
  m: StateManifest,
  sun: { azimuth: number; altitude: number },
  night: boolean,
) {
  const dem = layerURL(m, "terrain");
  if (!dem) return;
  const encoding = m.layers["terrain"]?.encoding ?? "mapbox";
  style.sources["dem"] = {
    type: "raster-dem",
    url: `pmtiles://${dem}`,
    encoding,
    tileSize: 256,
  };
  const belowHorizon = sun.altitude < 0;
  const azimuth = belowHorizon ? 315.0 : sun.azimuth;
  const altitude = belowHorizon ? 18.0 : Math.max(sun.altitude, 8);
  const dim = night || belowHorizon;
  const shadowAlpha = dim ? 0.28 : 0.5;
  const highlightAlpha = dim ? 0.1 : 0.28;
  const insertAt = style.layers.findIndex((l) => l.id === "background") + 1;
  style.layers.splice(Math.max(insertAt, 0), 0, {
    id: "live-hillshade",
    type: "hillshade",
    source: "dem",
    paint: {
      "hillshade-method": "igor",
      "hillshade-illumination-anchor": "map",
      "hillshade-illumination-direction": azimuth,
      "hillshade-illumination-altitude": altitude,
      "hillshade-exaggeration": dim ? 0.28 : 0.5,
      "hillshade-shadow-color": `rgba(59, 50, 38, ${shadowAlpha})`,
      "hillshade-highlight-color": `rgba(246, 239, 226, ${highlightAlpha})`,
      "hillshade-accent-color": `rgba(59, 50, 38, ${shadowAlpha * 0.6})`,
    },
  });
}

// -------------------------------------------------------------- markings

/** Map markings (Sep 16 2026 — see map/mapMarkings.ts for the why and for
 * the multipliers themselves). Runs LAST, after every look block, after
 * geology and PLSS, and before firstlight.ts's dead-source scrub — the
 * same slot the Swift side gives it, and for the same reason: it has to
 * see the FINAL width/size/halo of every layer, including the ones the
 * looks inject (hiking-line-casing, blm-trail-casing), the ones they
 * replace (the dustlarkTopo contour ladder), and the ones they retune (the
 * Satellite halo table), so its multipliers compose with all of them
 * instead of being overwritten by whichever block ran after it.
 *
 * Five classes, by layer id — explicit sets, never a prefix test: a
 * "mvum-" prefix would sweep mvum-road-label (a HERO LABEL, scaled by text
 * size, not by line width) in with mvum-road, and that kind of silent
 * miscategorisation is exactly what an explicit table prevents.
 *
 * `standard` returns before touching a single layer, so the generated
 * style is byte-identical to a build that never passed `markings` at all
 * (tests/map-markings.test.ts asserts that on the whole style JSON). */

/** Route numbers, trail names, street names, places, peaks, POIs, water
 * names — what a rider actually reads off the map at speed. Symbology v2
 * (SPEC.md section 11): "markingsHeroLabels gains stream-name*, camp-label,
 * fuel-label, viewpoint-label, trailhead-parking-label, peak-label"
 * (peak-label was already hero). poi-label is gone — split into
 * camp-label/ridb-facility-label; only camp-label re-joins hero, per the
 * same note (ridb-facility-label stays an un-themed secondary label). */
const HERO_LABELS = new Set([
  "mvum-road-label",
  "mvum-trail-label",
  "hiking-label",
  "street-label",
  "peak-label",
  "water-name",
  "place-village",
  "place-town",
  "stream-name-river",
  "stream-name",
  "camp-label",
  "fuel-label",
  "viewpoint-label",
  "trailhead-parking-label",
]);

/** Ownership, geology, PLSS sections, contour values, and (Symbology v2,
 * same note) wilderness/protected/gate/saddle labels — they grow, but stay
 * BEHIND the hero labels (referenceTextScale < textScale on every theme),
 * so making the map legible never turns it into a wall of type.
 * dtopo-contour-label is gone (section 6: the topo looks recolor the
 * template's own contour-label instead of injecting a parallel one) —
 * contour-label takes its place here. */
const REFERENCE_LABELS = new Set([
  "own-label",
  "own-label-line",
  "geology-label",
  "plss-section-label",
  "contour-label",
  "wilderness-label",
  "protected-label",
  "gate-label",
  "saddle-label",
]);

/** The legal network (every v2 class sourced from `trails`/`blm`/`hiking`)
 * and its casings AND, over imagery, each one's sleeve — sleeve/core/fill
 * scale by the SAME factor so the two-tone outline stays an outline instead
 * of swelling into a halo around a line that didn't move. mvum-trail(-
 * casing) is gone (split into the four-way class list below); ids that
 * aren't in the style simply never match, which is why every BLM id is
 * listed even though a given region may carry none of them. */
const HERO_LINES = new Set([
  "mvum-road",
  "mvum-road-casing",
  "mvum-road-sleeve",
  "mvum-road-hc",
  "mvum-road-hc-casing",
  "mvum-road-hc-sleeve",
  "mvum-paved",
  "mvum-paved-casing",
  "mvum-paved-sleeve",
  "mvum-trail-4x4",
  "mvum-trail-4x4-casing",
  "mvum-trail-4x4-sleeve",
  "mvum-trail-atv",
  "mvum-trail-atv-casing",
  "mvum-trail-atv-sleeve",
  "mvum-trail-moto",
  "mvum-trail-moto-casing",
  "mvum-trail-moto-sleeve",
  "blm-road",
  "blm-road-casing",
  "blm-road-sleeve",
  "blm-primitive",
  "blm-primitive-casing",
  "blm-primitive-sleeve",
  "blm-trail",
  "blm-trail-casing",
  "blm-trail-sleeve",
  "hiking-line",
  "hiking-line-casing",
  "hiking-line-sleeve",
  "hiking-line-horse",
]);

/** Basemap roads and the generic foot path, plus each one's imagery
 * sleeve — they carry the map's shape, so they grow too, but by less than
 * the network a rider is navigating by (baseLineScale < heroLineScale on
 * every theme). road-minor(-casing) is gone, split by Symbology v2 into
 * road-local-paved (paved minor/unclassified), road-service (no casing)
 * and road-track (no casing, unverified — deliberately NOT a base line: an
 * unverified two-track growing alongside the paved network would blur the
 * "legality unknown" read it exists to carry, so it's left unscaled like
 * the reference tier). */
const BASE_LINES = new Set([
  "road-local-paved",
  "road-local-paved-casing",
  "road-local-paved-sleeve",
  "road-secondary",
  "road-secondary-casing",
  "road-secondary-sleeve",
  "road-major",
  "road-major-casing",
  "road-major-sleeve",
  "path",
  "path-casing",
  "path-sleeve",
]);

/** Contours, boundaries, parcels, the PLSS grid: reference furniture. They
 * are NOT scaled at all — thickening them would fight the very lines this
 * setting exists to make findable. High contrast alone touches them, and
 * only to step them BACK (line-opacity x 0.8). contour-index joins contour
 * here (Symbology v2 section 6 splits the old single `contour` line into
 * intermediate/index weights; both stay reference tier). */
const REFERENCE_LINES = new Set([
  "contour",
  "contour-index",
  "boundary",
  "parcel-line",
  "plss-township-line",
  "plss-section-line",
]);

/** Symbology v2 (SPEC.md section 5, "High contrast under v2"): "no colour
 * overrides. `markingsHighContrastInk` ... and `markingsHighContrastCasingLight/
 * Imagery` ... are dropped; the theme is multipliers + Bold face only" — the
 * v1 network hue swap and its casing-color follow-up are both gone (the v2
 * ladder is already colorblind-separated without a special-case palette,
 * and repainting casings would fight the two-tone imagery construction
 * above). MARKINGS_TABLE.highContrastColors is now `{}`, so this list is
 * empty too — kept as a (typed, always-empty) set rather than deleted so
 * the recolor loop below and its test contract don't need a shape change
 * if a future theme ever needs one again. */
const HERO_CASINGS: string[] = [];

/** The five layer classes above, exported for tests/map-markings.test.ts —
 * the cross-platform contract pins them against the Swift literals in
 * ios/Dustlark/Map/FirstLightStyle.swift, where a set that quietly gained a
 * member on one platform is invisible in any rendered style (an id that
 * isn't in the style simply never matches). Not for production callers:
 * applyMarkings below is the only thing that should read these. */
export const MARKINGS_CLASSES = {
  heroLabels: HERO_LABELS,
  referenceLabels: REFERENCE_LABELS,
  heroLines: HERO_LINES,
  baseLines: BASE_LINES,
  referenceLines: REFERENCE_LINES,
  heroCasings: new Set(HERO_CASINGS),
};

/** Scales the OUTPUT positions of a style value and nothing else.
 *
 * A plain number scales. An `["interpolate", <interpolation>, ["zoom"], z0,
 * v0, z1, v1, ...]` scales v0/v1/... and LEAVES z0/z1/... exactly where
 * they are — scaling a zoom stop would move the zoom at which a width
 * changes, which is a different (and wrong) edit entirely: Bold means "the
 * same map, drawn heavier", not "the same map at a different zoom". A
 * `["step", <input>, <default>, s0, v0, ...]` scales its default and every
 * output, and again leaves its stops alone. Anything else (a `match` on a
 * data property, a `case`, a literal array like a dash pattern) comes back
 * untouched rather than being walked speculatively. */
function scaleNumeric(value: any, k: number): any {
  if (typeof value === "number") return value * k;
  if (!Array.isArray(value) || value.length === 0) return value;
  const op = value[0];
  if (op === "interpolate" || op === "interpolate-hcl" || op === "interpolate-lab") {
    // [op, interpolation, input, stop, out, stop, out, ...]
    const out: any[] = value.slice(0, 3);
    for (let i = 3; i + 1 < value.length; i += 2) {
      out.push(value[i], scaleNumeric(value[i + 1], k));
    }
    return out;
  }
  if (op === "step") {
    // [step, input, default, stop, out, stop, out, ...]
    const out: any[] = [value[0], value[1], scaleNumeric(value[2], k)];
    for (let i = 3; i + 1 < value.length; i += 2) {
      out.push(value[i], scaleNumeric(value[i + 1], k));
    }
    return out;
  }
  return value;
}

/** Multiplies one paint/layout property in place. An absent property stays
 * absent (never invented at a scaled default), and a *-dasharray is refused
 * outright: dash numbers are in LINE-WIDTH units, so a wider line already
 * lengthens the dash proportionally — scaling the array too would square
 * the effect and march a dashed trail toward solid as the theme climbs. No
 * caller passes one today; the guard is here so a future class list can't
 * quietly introduce one. */
function scaleProp(layer: AnyLayer, section: "paint" | "layout", key: string, k: number) {
  if (key.endsWith("-dasharray")) return;
  const props = layer[section];
  if (!props || props[key] === undefined) return;
  props[key] = scaleNumeric(props[key], k);
}

/** MapLibre's own default for an absent text-padding. */
const MAPLIBRE_TEXT_PADDING = 2;

/** text-padding is the clear space a label demands around itself before
 * MapLibre drops it. Growing the type without relaxing the padding hides
 * labels exactly where the rider asked for bigger ones, so the padding is
 * DIVIDED by the same factor the text was multiplied by: the label claims
 * the same ground it always did, just with more of that ground spent on
 * glyphs.
 *
 * Unlike `scaleProp`, an ABSENT padding is not left alone: it means
 * MapLibre's default of 2, and that 2 is relaxed and written explicitly.
 * Only own-label/own-label-line declare a padding in the template; every
 * hero label the setting exists for has none, and skipping them (as the
 * first cut did) kept the default 2 under 1.3x/1.55x glyphs and dropped
 * labels on the planner that the phone still drew (Sep 17 2026 review).
 * The arithmetic mirrors FirstLightStyle.markingsRelaxPadding exactly —
 * a 1.0 floor and 2-dp rounding — so both platforms write the same literal
 * and map-markings.test.ts can pin it. */
function relaxTextPadding(layer: AnyLayer, k: number) {
  if (k === 1) return;
  const layout = (layer.layout ??= {});
  const current = layout["text-padding"] === undefined ? MAPLIBRE_TEXT_PADDING : layout["text-padding"];
  layout["text-padding"] =
    typeof current === "number"
      ? Math.max(1, Math.round((current / k) * 100) / 100)
      : scaleNumeric(current, 1 / k);
}

/** The one hero label the high-contrast bold face leaves alone: water
 * names keep Noto-Sans-Italic — the slant is what says "water" on a map,
 * and a bold upright creek name reads as a road. Size and halo still
 * scale; only the face is exempt. Mirrors FirstLightStyle's
 * `markingsItalicHeroLabel`. */
const ITALIC_HERO_LABEL = "water-name";

/** MapLibre clamps a text halo to 1/4 of the font size when it renders —
 * anything above that is a number the style states and the map ignores.
 * Symbology v2's halo rule (SPEC.md section 7 / the section 10 halo test)
 * takes the ceiling as 0.24 of the final size, just inside the renderer's
 * own, so the style never claims a halo it will not get. */
const HALO_SIZE_CEILING = 0.24;

/** Smallest numeric output a scaled style value can produce — the binding
 * zoom for the clamp, since a halo written as one number has to sit under
 * the ceiling at EVERY zoom, and text-size is the thing that varies. */
function minOutput(value: any): number | null {
  if (typeof value === "number") return value;
  if (!Array.isArray(value) || value.length === 0) return null;
  const op = value[0];
  const outputs: any[] = [];
  if (op === "interpolate" || op === "interpolate-hcl" || op === "interpolate-lab") {
    // [op, interpolation, input, stop, OUT, stop, OUT, ...]
    for (let i = 4; i < value.length; i += 2) outputs.push(value[i]);
  } else if (op === "step") {
    // [step, input, DEFAULT, stop, OUT, stop, OUT, ...]
    outputs.push(value[2]);
    for (let i = 4; i < value.length; i += 2) outputs.push(value[i]);
  } else {
    return null;
  }
  let min: number | null = null;
  for (const out of outputs) {
    const n = minOutput(out);
    if (n !== null && (min === null || n < min)) min = n;
  }
  return min;
}

/** Applies every numeric output through `f`, keeping the value's shape. */
function mapOutputs(value: any, f: (n: number) => number): any {
  if (typeof value === "number") return f(value);
  if (!Array.isArray(value) || value.length === 0) return value;
  const op = value[0];
  if (op === "interpolate" || op === "interpolate-hcl" || op === "interpolate-lab") {
    const out: any[] = value.slice(0, 3);
    for (let i = 3; i + 1 < value.length; i += 2) out.push(value[i], mapOutputs(value[i + 1], f));
    return out;
  }
  if (op === "step") {
    const out: any[] = [value[0], value[1], mapOutputs(value[2], f)];
    for (let i = 3; i + 1 < value.length; i += 2) out.push(value[i], mapOutputs(value[i + 1], f));
    return out;
  }
  return value;
}

/** Holds text-halo-width under HALO_SIZE_CEILING x the FINAL text-size,
 * after both have been scaled.
 *
 * It bites on exactly one ground: over imagery, applyLook has already set
 * every hero label's halo to 0.22 of its template size, and Bold (1.6 halo
 * over 1.3 text) and High contrast (2.0 over 1.62) both push that ratio to
 * ~0.27 — past the renderer's ceiling, so the phone and the planner drew a
 * halo neither of them had asked for. On paper the template's own 0.15
 * fraction lands at ~0.185 scaled and nothing moves.
 *
 * Rounded to 2 dp, the way relaxTextPadding rounds, so both platforms write
 * the same literal; the iOS half implements the identical rule. An absent
 * halo stays absent — this only ever lowers a number that is already
 * there, it never invents one. */
function clampHalo(layer: AnyLayer) {
  const halo = layer.paint?.["text-halo-width"];
  if (halo === undefined) return;
  const size = minOutput(layer.layout?.["text-size"]);
  if (size === null) return;
  const ceiling = Math.round(HALO_SIZE_CEILING * size * 100) / 100;
  layer.paint["text-halo-width"] = mapOutputs(halo, (n) => Math.min(n, ceiling));
}

function applyLabel(layer: AnyLayer, textScale: number, haloScale: number, bold: boolean) {
  scaleProp(layer, "layout", "text-size", textScale);
  scaleProp(layer, "paint", "text-halo-width", haloScale);
  clampHalo(layer);
  relaxTextPadding(layer, textScale);
  if (
    bold &&
    String(layer.id ?? "") !== ITALIC_HERO_LABEL &&
    layer.layout &&
    layer.layout["text-font"] !== undefined
  ) {
    layer.layout["text-font"] = [...MARKINGS_TABLE.boldLabelFontStack];
  }
}

export function applyMarkings(
  style: StyleJSON,
  markings: MapMarkings,
  look: string,
  night: boolean,
) {
  const m = MARKINGS[markings];
  if (!m || markings === "standard") return;

  for (const layer of style.layers) {
    const id = String(layer.id ?? "");
    if (HERO_LABELS.has(id)) {
      applyLabel(layer, m.textScale, m.haloScale, m.boldLabelFont);
    } else if (REFERENCE_LABELS.has(id)) {
      // Reference labels keep the template's own face even on high
      // contrast — bolding them would undo the whole point of holding them
      // a step behind the hero labels.
      applyLabel(layer, m.referenceTextScale, m.haloScale, false);
    } else if (HERO_LINES.has(id)) {
      scaleProp(layer, "paint", "line-width", m.heroLineScale);
    } else if (BASE_LINES.has(id)) {
      scaleProp(layer, "paint", "line-width", m.baseLineScale);
    } else if (REFERENCE_LINES.has(id) && markings === "highContrast") {
      // Step back, don't grow. An absent line-opacity defaults to 1, so the
      // scaled value is written explicitly rather than skipped — otherwise
      // the one reference line without an explicit opacity (there is one in
      // the template today, and looks inject more) would be the only one
      // that never steps back.
      const opacity = layer.paint?.["line-opacity"];
      layer.paint = {
        ...(layer.paint ?? {}),
        "line-opacity": scaleNumeric(
          opacity === undefined ? 1 : opacity,
          MARKINGS_TABLE.referenceLineOpacityScale,
        ),
      };
    }
  }

  // The recolour is as much a LOOK decision as a markings one: these hexes
  // are tuned to sit on a photograph, on First Light's cream, and on shaded
  // relief. On the paper looks (Dustlark Topo, US Topo, 1950s Topo) the
  // whole point of the ground is that it IS the USGS sheet, and a
  // fluorescent network over it reads as damage rather than emphasis; at
  // night every colour in the style has already been remapped to the Night
  // Stage's dark palette, and dropping daylight-saturated hexes back in
  // would blow the dark adaptation the Night Stage exists to protect.
  // Widths and label sizes still apply everywhere — only the colours are
  // gated.
  if (!m.recoloursNetwork) return;
  if (night || !MARKINGS_TABLE.colourLooks.includes(look)) return;

  const hexes = MARKINGS_TABLE.highContrastColors;
  for (const layer of style.layers) {
    const hex = hexes[String(layer.id ?? "")];
    if (hex) layer.paint = { ...(layer.paint ?? {}), "line-color": hex };
  }
  // White sleeve on the light grounds, near-black over imagery — ink stays
  // ink regardless of what the photograph underneath is doing, the same
  // argument IMAGERY_CASING_INK is already built on (this just takes it
  // further, since high contrast is the theme for the worst light).
  const casing = look === "imagery" ? MARKINGS_TABLE.casingImagery : MARKINGS_TABLE.casingLight;
  for (const layer of style.layers) {
    if (HERO_CASINGS.includes(String(layer.id ?? ""))) {
      layer.paint = { ...(layer.paint ?? {}), "line-color": casing };
    }
  }
}
